import Slider from "@react-native-community/slider";
import { useRef, useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import {
  NEARBY_RADIUS_MAX_M,
  NEARBY_RADIUS_MID_M,
  NEARBY_RADIUS_MIN_M,
  SLIDER_POSITION_MAX,
  formatRadiusM,
  positionToRadiusM,
  radiusMToPosition,
} from "@/lib/nearby-radius";

/**
 * The people-discovery radius control — one slider in place of the old
 * three-chip picker, in the Mood Jar intensity slider's shape (a labelled
 * track with the current choice announced beside the heading).
 *
 * The track is piecewise: Close by (500 m) at the left edge, Nearby (5 km)
 * at the exact centre, Local area (10 km) at the right — see
 * `lib/nearby-radius.ts` for why the centre anchor forces that. Values snap
 * to 50 m notches left of centre and 100 m notches right of it, so "only
 * people within 1.5 km" is a notch on the left half rather than a choice the
 * old tiers rounded away.
 *
 * `onChange` fires per snapped value while dragging (for the live label);
 * `onCommit` fires once on release — the one to persist from, so a drag does
 * not write a profile update per notch. Return the save's promise from
 * `onCommit`: the thumb holds the released position until it settles, because
 * the `valueM` prop still carries the old radius while the write round-trips
 * and releasing early flashes the thumb back to where the drag started.
 */
export function NearbyRadiusSlider({
  valueM,
  onChange,
  onCommit,
  colors,
}: {
  valueM: number;
  onChange?: (radiusM: number) => void;
  onCommit: (radiusM: number) => void | Promise<void>;
  colors: {
    track: string;
    trackRemaining: string;
    thumb: string;
    label: string;
    labelMuted: string;
  };
}) {
  const { t } = useTranslation();
  // The slider owns its position while a finger is down OR a commit is in
  // flight; the prop wins otherwise. Without the first, a parent re-render
  // mid-drag snaps the thumb back under the user's finger; without the
  // second, release flashes the thumb back to the pre-drag value until the
  // save round-trips.
  const [dragPosition, setDragPosition] = useState<number | null>(null);
  // A live finger owns the thumb: a commit that settles mid-drag must not
  // release the hold out from under it.
  const dragToken = useRef(0);

  const position = dragPosition ?? radiusMToPosition(valueM);
  const shownM = positionToRadiusM(position);
  const withinLabel = t("home.nearbyRange.within", {
    distance: formatRadiusM(shownM),
  });

  return (
    <View>
      <Text style={[styles.value, { color: colors.label }]}>{withinLabel}</Text>
      <Slider
        value={position}
        minimumValue={0}
        maximumValue={SLIDER_POSITION_MAX}
        step={1}
        onValueChange={(next) => {
          setDragPosition(next);
          onChange?.(positionToRadiusM(next));
        }}
        onSlidingStart={() => {
          dragToken.current += 1;
        }}
        onSlidingComplete={(next) => {
          const token = dragToken.current;
          setDragPosition(next);
          void Promise.resolve(onCommit(positionToRadiusM(next)))
            // Errors are the caller's to report; here only the hold matters.
            // On failure the prop never moves, so this release snaps the
            // thumb back to the real value — honestly, next to the caller's
            // error notice.
            .catch(() => {})
            .finally(() => {
              if (dragToken.current === token) setDragPosition(null);
            });
        }}
        minimumTrackTintColor={colors.track}
        maximumTrackTintColor={colors.trackRemaining}
        thumbTintColor={colors.thumb}
        accessibilityLabel={t("home.filters.nearbyRange")}
        accessibilityValue={{
          min: 0,
          max: SLIDER_POSITION_MAX,
          now: Math.round(position),
          text: withinLabel,
        }}
        style={styles.slider}
      />
      {/*
        The distances are interpolated from the constants, never written into
        the translated string. They used to be baked in — "Close by · 200 m" —
        and when the bounds moved to 500/5000/10000 the labels kept promising
        the old ranges in all seven languages, which is a wrong promise about
        who can see you rather than a stale comment. Formatting them here means
        a bounds change cannot leave the copy behind, in any locale.
      */}
      <View style={styles.anchors}>
        <Text style={[styles.anchor, { color: colors.labelMuted }]}>
          {t("home.nearbyRange.closeBy", {
            distance: formatRadiusM(NEARBY_RADIUS_MIN_M),
          })}
        </Text>
        <Text
          style={[styles.anchor, styles.anchorMid, { color: colors.labelMuted }]}
        >
          {t("home.nearbyRange.nearby", {
            distance: formatRadiusM(NEARBY_RADIUS_MID_M),
          })}
        </Text>
        <Text
          style={[styles.anchor, styles.anchorEnd, { color: colors.labelMuted }]}
        >
          {t("home.nearbyRange.localArea", {
            distance: formatRadiusM(NEARBY_RADIUS_MAX_M),
          })}
        </Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  value: { fontSize: 14, fontWeight: "600", marginBottom: 4 },
  // No negative margin: it pushed the ends of the control past the section's
  // content edge, where an end-stop thumb could be clipped to a half circle.
  slider: { height: 28 },
  anchors: { flexDirection: "row", marginTop: 2 },
  anchor: { flex: 1, fontSize: 11 },
  anchorMid: { textAlign: "center" },
  anchorEnd: { textAlign: "right" },
});
